/**
 * Demo Query Buttons Component
 * Displays predefined query buttons for demo mode initial screen
 */

import { Spin, Alert, Typography } from 'antd'
import { PlayCircle } from 'lucide-react'
import { useDemoQueries } from '@/api/demo.api'
import { t } from '@/config/translations'
import styles from './DemoQueryButtons.module.css'

const { Title, Text } = Typography

export interface DemoQueryButtonsProps {
  onSelectQuery: (query: string) => void
  disabled?: boolean
}

export function DemoQueryButtons({ onSelectQuery, disabled = false }: DemoQueryButtonsProps) {
  const { data, isLoading, error } = useDemoQueries()

  if (isLoading) {
    return (
      <div className={styles['container']}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px', color: '#666' }}>Loading demo queries...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles['container']}>
        <Alert
          title="Error loading demo queries"
          description="Please try refreshing the page"
          type="error"
          showIcon
        />
      </div>
    )
  }

  const queries = data?.queries || []

  return (
    <div className={styles['container']}>
      <div className={styles['header']}>
        <Title level={3} className={styles['title']}>
          {t('demo_select_query')}
        </Title>
        <Text type="secondary" className={styles['subtitle']}>
          Explore SpacePort: one prepared example, then three live questions with OpenAI
        </Text>
      </div>

      <div className={styles['queriesWrapper']}>
        {queries.map((query) => (
          <button
            key={query.id}
            type="button"
            className={[styles['queryChip'], disabled && styles['queryChipDisabled']].filter(Boolean).join(' ')}
            onClick={() => {
              if (!disabled) {
                onSelectQuery(query.text)
              }
            }}
            disabled={disabled}
            title={query.text}
          >
            <span className={styles['queryIcon']}>
              <PlayCircle size={14} strokeWidth={2} />
            </span>
            <span className={styles['queryLabel']}>{query.text}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
